import ApplianceIcon from '@/components/Icons/ApplianceIcon';
import BikeStorageIcon from '@/components/Icons/BikeStorageIcon';
import ClosetIcon from '@/components/Icons/ClosetIcon';
import ClubhouseIcon from '@/components/Icons/ClubhouseIcon';
import CountertopIcon from '@/components/Icons/CountertopIcon';
import DogParkIcon from '@/components/Icons/DogParkIcon';
import EnergyEfficiencyIcon from '@/components/Icons/EnergyEfficiencyIcon';
import EntertainmentIcon from '@/components/Icons/EntertainmentIcon';
import EvChargingIcon from '@/components/Icons/EvChargingIcon';
import EventIcon from '@/components/Icons/EventIcon';
import FencedYardIcon from '@/components/Icons/FencedYardIcon';
import FireplaceIcon from '@/components/Icons/FireplaceIcon';
import FitnessIcon from '@/components/Icons/FitnessIcon';
import FlooringIcon from '@/components/Icons/FlooringIcon';
import LandscapingIcon from '@/components/Icons/LandscapingIcon';
import LedLightingIcon from '@/components/Icons/LedLightingIcon';
import MaintenanceIcon from '@/components/Icons/MaintenanceIcon';
import ManagementIcon from '@/components/Icons/ManagementIcon';
import OpenConceptIcon from '@/components/Icons/OpenConceptIcon';
import OutdoorFitnessIcon from '@/components/Icons/OutdoorFitnessIcon';
import PackageLockersIcon from '@/components/Icons/PackageLockersIcon';
import ParkingIcon from '@/components/Icons/ParkingIcon';
import PatioBalconyIcon from '@/components/Icons/PatioBalconyIcon';
import PlaygroundIcon from '@/components/Icons/PlaygroundIcon';
import PoolIcon from '@/components/Icons/PoolIcon';
import RecyclingIcon from '@/components/Icons/RecyclingIcon';
import SecurityIcon from '@/components/Icons/SecurityIcon';
import SmartLockIcon from '@/components/Icons/SmartLockIcon';
import SupportIcon from '@/components/Icons/SupportIcon';
import TrashIcon from '@/components/Icons/TrashIcon';
import WalkingTrailIcon from '@/components/Icons/WalkingTrailIcon';
import WasherDryerIcon from '@/components/Icons/WasherDryerIcon';
import WaterSavingIcon from '@/components/Icons/WaterSavingIcon';
import WifiIcon from '@/components/Icons/WifiIcon';
import YogaIcon from '@/components/Icons/YogaIcon';
import type { Address } from '@/domain/addresses';
import type { PropifyEntity } from '@/domain/base';
import type { File } from '@/domain/files';
import type { DayOfWeek } from '@/utils/dates';
import type { FC } from 'react';

enum CommunityAmenityCategory {
  LIFESTYLE_COMMUNITY = 'LIFESTYLE_COMMUNITY',
  FITNESS_WELLNESS = 'FITNESS_WELLNESS',
  SMART_HOME_TECH = 'SMART_HOME_TECH',
  CONVENIENCE_EVERYDAY_LIVING = 'CONVENIENCE_EVERYDAY_LIVING',
  SUSTAINABILITY = 'SUSTAINABILITY',
  PREMIUM_HOME_FEATURES = 'PREMIUM_HOME_FEATURES',
}

export enum CommunityAmenity {
  POOL = 'POOL',
  OUTDOOR_FIREPLACE = 'OUTDOOR_FIREPLACE',
  OUTDOOR_ENTERTAINMENT = 'OUTDOOR_ENTERTAINMENT',
  CLUBHOUSE = 'CLUBHOUSE',
  RESIDENT_EVENTS = 'RESIDENT_EVENTS',
  PET_FRIENDLY_DOG_PARK_AND_PET_WASHING_STATION = 'PET_FRIENDLY_DOG_PARK_AND_PET_WASHING_STATION',
  WALKING_TRAILS = 'WALKING_TRAILS',
  CHILDREN_S_PLAY_AREA = 'CHILDREN_S_PLAY_AREA',
  PARCEL_PACKAGE_LOCKERS = 'PARCEL_PACKAGE_LOCKERS',
  FITNESS_CENTER = 'FITNESS_CENTER',
  YOGA_STUDIO_CLASSES = 'YOGA_STUDIO_CLASSES',
  OUTDOOR_FITNESS_STATIONS = 'OUTDOOR_FITNESS_STATIONS',
  BIKE_STORAGE = 'BIKE_STORAGE',
  SMART_LOCKS_THERMOSTATS_HOME_AUTOMATION = 'SMART_LOCKS_THERMOSTATS_HOME_AUTOMATION',
  HIGH_SPEED_FIBER_INTERNET = 'HIGH_SPEED_FIBER_INTERNET',
  APP_BASED_MAINTENANCE_REQUESTS_RESIDENT_PORTAL = 'APP_BASED_MAINTENANCE_REQUESTS_RESIDENT_PORTAL',
  SMART_HOME_SECURITY_OPTIONS = 'SMART_HOME_SECURITY_OPTIONS',
  PROFESSIONAL_ON_SITE_OR_DEDICATED_MANAGEMENT_TEAM = 'PROFESSIONAL_ON_SITE_OR_DEDICATED_MANAGEMENT_TEAM',
  MAINTENANCE_TEAM_WITH_24_7_EMERGENCY_SUPPORT = 'MAINTENANCE_TEAM_WITH_24_7_EMERGENCY_SUPPORT',
  VALET_TRASH = 'VALET_TRASH',
  LANDSCAPING_SERVICE_PACKAGES = 'LANDSCAPING_SERVICE_PACKAGES',
  COMMUNITY_WI_FI_IN_COMMON_AREAS = 'COMMUNITY_WI_FI_IN_COMMON_AREAS',
  RESERVED_PARKING = 'RESERVED_PARKING',
  EV_CHARGING_STATIONS = 'EV_CHARGING_STATIONS',
  ENERGY_EFFICIENT_APPLIANCES_HVAC = 'ENERGY_EFFICIENT_APPLIANCES_HVAC',
  WATER_SAVING_LANDSCAPING = 'WATER_SAVING_LANDSCAPING',
  LED_LIGHTING_THROUGHOUT = 'LED_LIGHTING_THROUGHOUT',
  RECYCLING_STATIONS = 'RECYCLING_STATIONS',
  OPEN_CONCEPT_FLOOR_PLANS = 'OPEN_CONCEPT_FLOOR_PLANS',
  STAINLESS_STEEL_APPLIANCES = 'STAINLESS_STEEL_APPLIANCES',
  QUARTZ_OR_GRANITE_COUNTERTOPS = 'QUARTZ_OR_GRANITE_COUNTERTOPS',
  LUXURY_VINYL_PLANK_FLOORING = 'LUXURY_VINYL_PLANK_FLOORING',
  IN_HOME_WASHER_DRYER = 'IN_HOME_WASHER_DRYER',
  WALK_IN_CLOSETS = 'WALK_IN_CLOSETS',
  FENCED_BACKYARDS = 'FENCED_BACKYARDS',
  COVERED_PATIOS_OR_BALCONIES = 'COVERED_PATIOS_OR_BALCONIES',
}

export interface CommunityFAQ {
  question: string;
  answer: string;
}

interface CommunityContactInformation {
  email?: string;
  phone?: string;
  instagramURL?: string;
  xURL?: string;
  facebookURL?: string;
}

interface CommunityWebsiteDetails {
  heroSectionMainText: string;
  heroSectionSubtext: string;
  bannerText?: string;
  showBanner?: boolean;
}

type OfficeHours = Record<DayOfWeek, [string, string]>;

export interface Community extends PropifyEntity {
  name: string;
  logo?: File;
  hero?: File;
  map?: File;
  favicon?: File;
  applicationRequirements?: string;
  contactInformation?: CommunityContactInformation;
  websiteDetails?: CommunityWebsiteDetails;
  faqs?: CommunityFAQ[];
  advertisedAmenities?: CommunityAmenity[];
  address?: Address;
  officeHours?: OfficeHours;
}

interface CommunityAmenityDetails {
  name: string;
  description: string;
  icon: FC<{ className?: string }>;
  category: CommunityAmenityCategory;
}

export const AMENITIES_DETAILS: Record<CommunityAmenity, CommunityAmenityDetails> = {
  [CommunityAmenity.POOL]: {
    name: 'Pool',
    description: 'Enjoy a refreshing swim in our community pool.',
    icon: PoolIcon,
    category: CommunityAmenityCategory.LIFESTYLE_COMMUNITY,
  },
  [CommunityAmenity.OUTDOOR_FIREPLACE]: {
    name: 'Outdoor Fireplace',
    description: 'Relax by the warmth of our outdoor fireplace.',
    icon: FireplaceIcon,
    category: CommunityAmenityCategory.LIFESTYLE_COMMUNITY,
  },
  [CommunityAmenity.OUTDOOR_ENTERTAINMENT]: {
    name: 'Outdoor Entertainment',
    description: 'Host gatherings in our outdoor entertainment areas.',
    icon: EntertainmentIcon,
    category: CommunityAmenityCategory.LIFESTYLE_COMMUNITY,
  },
  [CommunityAmenity.CLUBHOUSE]: {
    name: 'Clubhouse',
    description: 'Socialize and unwind in our community clubhouse.',
    icon: ClubhouseIcon,
    category: CommunityAmenityCategory.LIFESTYLE_COMMUNITY,
  },
  [CommunityAmenity.RESIDENT_EVENTS]: {
    name: 'Resident Events',
    description: 'Participate in exclusive events for our residents.',
    icon: EventIcon,
    category: CommunityAmenityCategory.LIFESTYLE_COMMUNITY,
  },
  [CommunityAmenity.PET_FRIENDLY_DOG_PARK_AND_PET_WASHING_STATION]: {
    name: 'Pet-Friendly Dog Park and Pet Washing Station',
    description: 'A dedicated space for your furry friends to play and get cleaned.',
    icon: DogParkIcon,
    category: CommunityAmenityCategory.LIFESTYLE_COMMUNITY,
  },
  [CommunityAmenity.WALKING_TRAILS]: {
    name: 'Walking Trails',
    description: 'Explore scenic walking trails within the community.',
    icon: WalkingTrailIcon,
    category: CommunityAmenityCategory.LIFESTYLE_COMMUNITY,
  },
  [CommunityAmenity.CHILDREN_S_PLAY_AREA]: {
    name: "Children's Play Area",
    description: 'A safe and fun area for children to play.',
    icon: PlaygroundIcon,
    category: CommunityAmenityCategory.LIFESTYLE_COMMUNITY,
  },
  [CommunityAmenity.PARCEL_PACKAGE_LOCKERS]: {
    name: 'Parcel Package Lockers',
    description: 'Secure lockers for convenient package delivery and pickup.',
    icon: PackageLockersIcon,
    category: CommunityAmenityCategory.LIFESTYLE_COMMUNITY,
  },
  [CommunityAmenity.FITNESS_CENTER]: {
    name: 'Fitness Center',
    description: 'Stay active with our fully-equipped fitness center.',
    icon: FitnessIcon,
    category: CommunityAmenityCategory.FITNESS_WELLNESS,
  },
  [CommunityAmenity.YOGA_STUDIO_CLASSES]: {
    name: 'Yoga Studio & Classes',
    description: 'Find your balance with yoga classes in our dedicated studio.',
    icon: YogaIcon,
    category: CommunityAmenityCategory.FITNESS_WELLNESS,
  },
  [CommunityAmenity.OUTDOOR_FITNESS_STATIONS]: {
    name: 'Outdoor Fitness Stations',
    description: 'Exercise outdoors with our fitness stations.',
    icon: OutdoorFitnessIcon,
    category: CommunityAmenityCategory.FITNESS_WELLNESS,
  },
  [CommunityAmenity.BIKE_STORAGE]: {
    name: 'Bike Storage',
    description: 'Secure storage for your bicycles.',
    icon: BikeStorageIcon,
    category: CommunityAmenityCategory.FITNESS_WELLNESS,
  },
  [CommunityAmenity.SMART_LOCKS_THERMOSTATS_HOME_AUTOMATION]: {
    name: 'Smart Locks, Thermostats & Home Automation',
    description: 'Control your home with smart technology.',
    icon: SmartLockIcon,
    category: CommunityAmenityCategory.SMART_HOME_TECH,
  },
  [CommunityAmenity.HIGH_SPEED_FIBER_INTERNET]: {
    name: 'High-Speed Fiber Internet',
    description: 'Enjoy fast and reliable fiber internet connectivity.',
    icon: WifiIcon,
    category: CommunityAmenityCategory.SMART_HOME_TECH,
  },
  [CommunityAmenity.APP_BASED_MAINTENANCE_REQUESTS_RESIDENT_PORTAL]: {
    name: 'App-Based Maintenance Requests & Resident Portal',
    description: 'Easily manage maintenance requests through our app.',
    icon: MaintenanceIcon,
    category: CommunityAmenityCategory.SMART_HOME_TECH,
  },
  [CommunityAmenity.SMART_HOME_SECURITY_OPTIONS]: {
    name: 'Smart Home Security Options',
    description: 'Keep your home safe with advanced security features.',
    icon: SecurityIcon,
    category: CommunityAmenityCategory.SMART_HOME_TECH,
  },
  [CommunityAmenity.PROFESSIONAL_ON_SITE_OR_DEDICATED_MANAGEMENT_TEAM]: {
    name: 'Professional On-Site or Dedicated Management Team',
    description: 'Our team is here to assist you with all your needs.',
    icon: ManagementIcon,
    category: CommunityAmenityCategory.CONVENIENCE_EVERYDAY_LIVING,
  },
  [CommunityAmenity.MAINTENANCE_TEAM_WITH_24_7_EMERGENCY_SUPPORT]: {
    name: 'Maintenance Team with 24/7 Emergency Support',
    description: 'Reliable maintenance services available around the clock.',
    icon: SupportIcon,
    category: CommunityAmenityCategory.CONVENIENCE_EVERYDAY_LIVING,
  },
  [CommunityAmenity.VALET_TRASH]: {
    name: 'Valet Trash',
    description: 'Convenient trash pickup services right from your doorstep.',
    icon: TrashIcon,
    category: CommunityAmenityCategory.CONVENIENCE_EVERYDAY_LIVING,
  },
  [CommunityAmenity.LANDSCAPING_SERVICE_PACKAGES]: {
    name: 'Landscaping Service Packages',
    description: 'Professional landscaping to keep our community beautiful.',
    icon: LandscapingIcon,
    category: CommunityAmenityCategory.CONVENIENCE_EVERYDAY_LIVING,
  },
  [CommunityAmenity.COMMUNITY_WI_FI_IN_COMMON_AREAS]: {
    name: 'Community Wi-Fi in Common Areas',
    description: 'Stay connected with Wi-Fi access in shared spaces.',
    icon: WifiIcon,
    category: CommunityAmenityCategory.CONVENIENCE_EVERYDAY_LIVING,
  },
  [CommunityAmenity.RESERVED_PARKING]: {
    name: 'Reserved Parking',
    description: 'Designated parking spots for residents.',
    icon: ParkingIcon,
    category: CommunityAmenityCategory.CONVENIENCE_EVERYDAY_LIVING,
  },
  [CommunityAmenity.EV_CHARGING_STATIONS]: {
    name: 'EV Charging Stations',
    description: 'Electric vehicle charging stations for eco-friendly living.',
    icon: EvChargingIcon,
    category: CommunityAmenityCategory.CONVENIENCE_EVERYDAY_LIVING,
  },
  [CommunityAmenity.ENERGY_EFFICIENT_APPLIANCES_HVAC]: {
    name: 'Energy-Efficient Appliances & HVAC',
    description: 'Reduce your carbon footprint with energy-saving appliances.',
    icon: EnergyEfficiencyIcon,
    category: CommunityAmenityCategory.SUSTAINABILITY,
  },
  [CommunityAmenity.WATER_SAVING_LANDSCAPING]: {
    name: 'Water-Saving Landscaping',
    description: 'Eco-friendly landscaping designed to conserve water.',
    icon: WaterSavingIcon,
    category: CommunityAmenityCategory.SUSTAINABILITY,
  },
  [CommunityAmenity.LED_LIGHTING_THROUGHOUT]: {
    name: 'LED Lighting Throughout',
    description: 'Energy-efficient LED lighting in all homes and common areas.',
    icon: LedLightingIcon,
    category: CommunityAmenityCategory.SUSTAINABILITY,
  },
  [CommunityAmenity.RECYCLING_STATIONS]: {
    name: 'Recycling Stations',
    description: 'Convenient recycling stations to promote sustainability.',
    icon: RecyclingIcon,
    category: CommunityAmenityCategory.SUSTAINABILITY,
  },
  [CommunityAmenity.OPEN_CONCEPT_FLOOR_PLANS]: {
    name: 'Open-Concept Floor Plans',
    description: 'Spacious and modern open-concept living spaces.',
    icon: OpenConceptIcon,
    category: CommunityAmenityCategory.PREMIUM_HOME_FEATURES,
  },
  [CommunityAmenity.STAINLESS_STEEL_APPLIANCES]: {
    name: 'Stainless Steel Appliances',
    description: 'Modern stainless steel appliances in every home.',
    icon: ApplianceIcon,
    category: CommunityAmenityCategory.PREMIUM_HOME_FEATURES,
  },
  [CommunityAmenity.QUARTZ_OR_GRANITE_COUNTERTOPS]: {
    name: 'Quartz or Granite Countertops',
    description: 'Elegant quartz or granite countertops in kitchens and bathrooms.',
    icon: CountertopIcon,
    category: CommunityAmenityCategory.PREMIUM_HOME_FEATURES,
  },
  [CommunityAmenity.LUXURY_VINYL_PLANK_FLOORING]: {
    name: 'Luxury Vinyl Plank Flooring',
    description: 'Durable and stylish luxury vinyl plank flooring throughout the home.',
    icon: FlooringIcon,
    category: CommunityAmenityCategory.PREMIUM_HOME_FEATURES,
  },
  [CommunityAmenity.IN_HOME_WASHER_DRYER]: {
    name: 'In-Home Washer & Dryer',
    description: 'Convenient in-home washer and dryer units.',
    icon: WasherDryerIcon,
    category: CommunityAmenityCategory.PREMIUM_HOME_FEATURES,
  },
  [CommunityAmenity.WALK_IN_CLOSETS]: {
    name: 'Walk-In Closets',
    description: 'Spacious walk-in closets for ample storage.',
    icon: ClosetIcon,
    category: CommunityAmenityCategory.PREMIUM_HOME_FEATURES,
  },
  [CommunityAmenity.FENCED_BACKYARDS]: {
    name: 'Fenced Backyards',
    description: 'Private fenced backyards for added security and privacy.',
    icon: FencedYardIcon,
    category: CommunityAmenityCategory.PREMIUM_HOME_FEATURES,
  },
  [CommunityAmenity.COVERED_PATIOS_OR_BALCONIES]: {
    name: 'Covered Patios or Balconies',
    description: 'Enjoy outdoor living with covered patios or balconies.',
    icon: PatioBalconyIcon,
    category: CommunityAmenityCategory.PREMIUM_HOME_FEATURES,
  },
};
